import React, { useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { DirectoryFilters } from '../components/directory/DirectoryFilters';
import { DirectoryGrid } from '../components/directory/DirectoryGrid';
import { Pagination } from '../components/directory/Pagination';
import { getAllCommunityMembers } from '../services/communityService';
import { CommunityMember, CommunityQueryParams } from '../types/community';
import { SparklesIcon, ArrowRightIcon, SearchIcon, CalendarIcon, HandshakeIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/Button';
export function OrganizersDirectoryPage() {
  const [members, setMembers] = useState<CommunityMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [queryParams, setQueryParams] = useState<CommunityQueryParams>({
    page: 1,
    limit: 12,
    type: 'organizer'
  });
  useEffect(() => {
    const fetchMembers = async () => {
      setLoading(true);
      try {
        const {
          data,
          totalPages
        } = await getAllCommunityMembers(queryParams);
        setMembers(data);
        setTotalPages(totalPages);
      } catch (error) {
        console.error('Failed to fetch organizers:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, [queryParams]);
  const handleFilterChange = (newParams: Partial<CommunityQueryParams>) => {
    setQueryParams(prev => ({
      ...prev,
      ...newParams,
      page: 1
    }));
  };
  const handlePageChange = (page: number) => {
    setQueryParams(prev => ({
      ...prev,
      page
    }));
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQueryParams(prev => ({
      ...prev,
      search: searchQuery,
      page: 1
    }));
  };
  return <div className="min-h-screen bg-gray-50" data-id="element-3164">
      <Navbar data-id="element-3165" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-700 py-16" data-id="element-3166">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3167">
          <div className="max-w-3xl mx-auto text-center" data-id="element-3168">
            <div className="inline-flex items-center px-3 py-1 bg-purple-500/30 text-purple-100 rounded-full text-sm font-medium mb-4" data-id="element-3169">
              <CalendarIcon className="h-4 w-4 mr-2" data-id="element-3170" />
              Organizer Directory
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-3171">
              Discover Event Organizers
            </h1>
            <p className="text-lg text-purple-100 mb-8" data-id="element-3172">
              Connect with event organizers looking for brand partnerships and
              sponsorships. Find your perfect match based on event type,
              audience, and marketing opportunities.
            </p>

            {/* Search Bar */}
            <form onSubmit={handleSearch} className="max-w-xl mx-auto mb-6" data-id="element-3173">
              <div className="relative" data-id="element-3174">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-3175" />
                <input type="text" placeholder="Search events by name, type, or location..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-xl border-0 shadow-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300" data-id="element-3176" />
              </div>
            </form>

            <div className="flex flex-wrap justify-center gap-4" data-id="element-3177">
              <Link to="/onboarding/brand" data-id="element-3178">
                <Button variant="outline" className="bg-white text-purple-600 border-white hover:bg-purple-50" data-id="element-3179">
                  <SparklesIcon className="h-5 w-5 mr-2" data-id="element-3180" />
                  I'm a Brand
                </Button>
              </Link>
              <Link to="/brands" data-id="element-3181">
                <Button variant="ghost" className="text-white border-white/30 hover:bg-white/10" data-id="element-3182">
                  Browse Brands
                  <ArrowRightIcon className="h-4 w-4 ml-2" data-id="element-3183" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Directory Section */}
      <section className="py-12" data-id="element-3184">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3185">
          <div className="flex flex-col lg:flex-row gap-8" data-id="element-3186">
            {/* Filters Sidebar */}
            <div className="w-full lg:w-72 flex-shrink-0" data-id="element-3187">
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-24" data-id="element-3188">
                <h3 className="font-semibold text-gray-900 mb-4" data-id="element-3189">Filters</h3>
                <DirectoryFilters onFilterChange={handleFilterChange} currentFilters={queryParams} memberType="organizer" data-id="element-3190" />
              </div>
            </div>

            {/* Main Content */}
            <div className="flex-1" data-id="element-3191">
              <div className="flex items-center justify-between mb-6" data-id="element-3192">
                <div data-id="element-3193">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-3194">
                    Browse Event Organizers
                  </h2>
                  <p className="text-sm text-gray-500 mt-1" data-id="element-3195">
                    {loading ? 'Loading...' : `${members.length} organizers found`}
                  </p>
                </div>
              </div>

              {loading ? <div className="flex justify-center items-center h-64" data-id="element-3196">
                  <div className="flex flex-col items-center" data-id="element-3197">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-200 border-t-purple-600 mb-4" data-id="element-3198"></div>
                    <p className="text-gray-500" data-id="element-3199">Loading organizers...</p>
                  </div>
                </div> : members.length === 0 ? <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center" data-id="element-3200">
                  <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3201">
                    <CalendarIcon className="h-8 w-8 text-gray-400" data-id="element-3202" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-3203">
                    No organizers found
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-3204">
                    Try adjusting your filters or search criteria to find more
                    event organizers.
                  </p>
                  <Button variant="outline" onClick={() => {
                setSearchQuery('');
                setQueryParams({
                  page: 1,
                  limit: 12,
                  type: 'organizer'
                });
              }} data-id="element-3205">
                    Clear Filters
                  </Button>
                </div> : <>
                  <DirectoryGrid members={members} data-id="element-3206" />
                  {totalPages > 1 && <div className="mt-8" data-id="element-3207">
                      <Pagination currentPage={queryParams.page || 1} totalPages={totalPages} onPageChange={handlePageChange} data-id="element-3208" />
                    </div>}
                </>}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white border-t border-gray-100" data-id="element-3209">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-3210">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-3211">
            Are you an event organizer looking for sponsors?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-3212">
            Join SponsrAI to get matched with brands that align with your
            event's audience and values.
          </p>
          <Link to="/onboarding/organizer" data-id="element-3213">
            <Button size="lg" rightIcon={<ArrowRightIcon className="h-5 w-5" data-id="element-3215" />} data-id="element-3214">
              Register Your Event
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8" data-id="element-3216">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3217">
          <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-3218">
            <div className="flex items-center mb-4 md:mb-0" data-id="element-3219">
              <div className="bg-blue-600 p-2 rounded-lg mr-3" data-id="element-3220">
                <HandshakeIcon className="h-5 w-5 text-white" data-id="element-3221" />
              </div>
              <span className="text-white font-semibold" data-id="element-3222">SponsrAI</span>
            </div>
            <div className="text-sm" data-id="element-3223">
              © {new Date().getFullYear()} SponsrAI. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>;
}